/**
 * The language the backend must answer a legacy request in.
 *
 * The API resolves it from the `x-lang` header and nothing else
 * (`HeaderLangResolver` -> `resolveLanguage`, `platform/monorepo` `apps/api`);
 * `Accept-Language` is never read and an absent header means `en-US`. The
 * legacy app sent no header at all, so every message the backend had already
 * translated still reached a Brazilian merchant in English (TWISM-19940).
 *
 * The business's country is the source of truth, as on the host
 * (`host/src/shared/hooks/useFetcher.ts`, `resolveRequestLang`): the merchant
 * has no language switcher, so the portal's language is a property of the
 * business, and the browser only answers for the window before the business is
 * hydrated. The two apps share no code, so this mirrors
 * `host/src/shared/domain/locale/market.ts` instead of importing it.
 *
 * That pre-hydration window is not empty: `getBusinessInfo()` is itself what
 * populates the store, so it and everything before it go out in the browser's
 * language — a Brazilian merchant on an English-configured browser gets that
 * first batch in English. The host mitigates the analogous case by seeding
 * `x-country` from localStorage; there is no equivalent here, and a message
 * from that window is rare enough not to warrant one.
 */
const FALLBACK_LANG = 'en-US';
const PT_BR = 'pt-BR';

/** Both the ISO alpha-2 code and the country name, because payloads carry either. */
const COUNTRY_TO_LANG: { [country: string]: string } = {
  BR: PT_BR,
  BRAZIL: PT_BR,
  US: FALLBACK_LANG,
  'UNITED STATES': FALLBACK_LANG
};

export interface IBusinessCountry {
  country?: string | null;
  countryIso2?: string | null;
}

let businessLang = '';

const resolveBrowserLang = (): string => {
  const browserLang =
    typeof navigator === 'undefined' ? '' : navigator.language || '';

  return browserLang.toLowerCase().indexOf('pt') === 0 ? PT_BR : FALLBACK_LANG;
};

/** Empty when the country says nothing; an unknown country is the US, as on the host. */
const resolveLangFromCountry = (country?: string | null): string => {
  if (!country) return '';

  return COUNTRY_TO_LANG[String(country).trim().toUpperCase()] || FALLBACK_LANG;
};

/**
 * Callers all over the legacy app dispatch a partial business
 * (`{ ...business, oneField }`), so a payload carrying no country is not a
 * merchant in an unknown country — it says nothing about the country, and the
 * language already resolved must survive it.
 */
export const setBusinessRequestLang = (
  business?: IBusinessCountry | null
): void => {
  if (!business) return;

  const lang = resolveLangFromCountry(business.countryIso2 || business.country);
  if (lang) businessLang = lang;
};

export const getRequestLang = (): string => businessLang || resolveBrowserLang();

/** Empty until a business with a country has been seen; unlike `getRequestLang`, never a browser guess. */
export const getBusinessLang = (): string => businessLang;

/** Test-only: the resolved language is module state and leaks between specs. */
export const resetRequestLang = (): void => {
  businessLang = '';
};
